import React, { useState, useCallback, useMemo, useRef } from 'react';
import AdminLayout from '../../components/AdminLayout';
import Loader from '../../components/Loader';
import { useNotification } from '../../context/NotificationContext';
import {
  createEngagementService,
  deleteEngagementService,
  getEngagementServices,
  updateEngagementService,
  duplicateEngagementService
} from '../../services/adminApi';
import { useAdminInfiniteList, useNearBottomScroll } from '../../hooks/useAdminInfiniteList';
import { useAdminListView } from '../../hooks/useAdminListView';
import AdminListViewControls from '../../components/AdminListViewControls';
import AdminSelectableList from '../../components/AdminSelectableList';
import CatalogItemActions from '../../components/CatalogItemActions';
import { normalizeAdminListResponse } from '../../utils/adminListResponse';
import ContentScopeFields from '../../components/ContentScopeFields';
import usePortalScope from '../../hooks/usePortalScope';
import {
  contentScopeFormForPortal,
  contentScopeAllowedScopes,
  initialContentScopeFilter,
  contentScopeFromItem,
  contentScopeToPayload,
  formatContentScopeLabel,
  contentScopeListParams
} from '../../utils/contentScope';
import './AdminPages.css';
import { isHttpUrl } from '../../utils/httpUrl';
import MediaUploadInput from '../../components/MediaUploadInput';
import ExpandableText from '../../components/ExpandableText';
import { removeConfirmMessage, deactivateConfirmMessage } from '../../utils/portalCopy';

const ANDROID_PACKAGE_RE = /^[a-zA-Z][a-zA-Z0-9_]*(\.[a-zA-Z][a-zA-Z0-9_]+)+$/;

const makeInitialForm = (portal) => ({
  title: '',
  linkUrl: '',
  appId: '',
  thumbnailUrl: '',
  description: '',
  displayOrder: 0,
  isEnabled: true,
  ...contentScopeFormForPortal(portal)
});

const EngagementServiceManagement = () => {
  const { showError, showSuccess, confirm } = useNotification();
  const portal = usePortalScope();
  const [filters, setFilters] = useState(initialContentScopeFilter());
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState(() => makeInitialForm(portal));
  const [formErrors, setFormErrors] = useState({});
  const formRef = useRef(null);

  const filterKey = useMemo(() => JSON.stringify({ ...filters, searchTerm }), [filters, searchTerm]);

  const loadEngagementPage = useCallback(async (params) => {
    const axiosRes = await getEngagementServices(params);
    const { data, pagination } = normalizeAdminListResponse(axiosRes);
    const normalized = data.map((s) => ({
      ...s,
      isEnabled: s.isEnabled !== false
    }));
    return { data: { data: normalized, pagination } };
  }, []);

  const getParams = useCallback(() => {
    const params = contentScopeListParams(portal, filters);
    if (searchTerm.trim()) params.search = searchTerm.trim();
    return params;
  }, [portal, filters, searchTerm]);

  const { items: services, loading, loadingMore, pagination, loadMore, reload } = useAdminInfiniteList({
    load: loadEngagementPage,
    resetKey: `engagement-services-${filterKey}`,
    getParams,
    pageSize: 30
  });

  const scrollRef = useNearBottomScroll(loadMore, { enabled: !loading && pagination.hasMore });

  const listView = useAdminListView(services, (item) => item._id, {
    defaultView: 'grid',
    resetKey: `engagement-services-${filterKey}`
  });

  const resetForm = () => {
    setFormData(makeInitialForm(portal));
    setFormErrors({});
    setEditingItem(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const link = String(formData.linkUrl || '').trim();
    const thumb = String(formData.thumbnailUrl || '').trim();
    const appId = String(formData.appId || '').trim();
    const errors = {};
    if (!String(formData.title || '').trim()) errors.title = 'Title is required';
    if (!isHttpUrl(link)) errors.linkUrl = 'Link URL must be a valid http(s) address';
    if (thumb && !isHttpUrl(thumb)) errors.thumbnailUrl = 'Thumbnail URL must be a valid http(s) address';
    if (appId && !ANDROID_PACKAGE_RE.test(appId)) {
      errors.appId = 'App ID must be a valid Android package name (e.g. com.netflix.ninja)';
    }
    if (formData.scope === 'brand' && !(formData.brandIds?.length || formData.brandId)) {
      errors.brands = 'Select at least one brand';
    }
    if (formData.scope === 'branch' && !(formData.salonIds?.length || formData.salonId)) {
      errors.salons = 'Select at least one branch';
    }
    if (Object.keys(errors).length) {
      setFormErrors(errors);
      showError('Please fix the highlighted fields and try again.');
      formRef.current?.querySelector('.input-error')?.focus?.();
      return;
    }
    setFormErrors({});
    try {
      const { scope, brandId, brandIds, salonId, salonIds, ...rest } = formData;
      const payload = {
        ...rest,
        linkUrl: link,
        appId,
        thumbnailUrl: thumb,
        displayOrder: Number(formData.displayOrder) || 0,
        ...contentScopeToPayload({ scope, brandId, brandIds, salonId, salonIds })
      };
      if (editingItem) {
        await updateEngagementService(editingItem._id, payload);
      } else {
        await createEngagementService(payload);
      }
      resetForm();
      showSuccess(editingItem ? 'Engagement service updated.' : 'Engagement service created.');
      await reload();
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to save engagement service');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      title: item.title || '',
      linkUrl: item.linkUrl || '',
      appId: item.appId || '',
      thumbnailUrl: item.thumbnailUrl || '',
      description: item.description || '',
      displayOrder: item.displayOrder || 0,
      isEnabled: !!item.isEnabled,
      ...contentScopeFromItem(item)
    });
    setShowForm(true);
  };

  const handleToggle = async (item) => {
    const nextEnabled = !item.isEnabled;
    if (!nextEnabled) {
      const ok = await confirm({
        title: 'Deactivate engagement service',
        message: deactivateConfirmMessage('engagement service'),
        confirmText: 'Deactivate',
        danger: true
      });
      if (!ok) return;
    }
    try {
      await updateEngagementService(item._id, { isEnabled: nextEnabled });
      showSuccess(nextEnabled ? 'Engagement service activated.' : 'Engagement service deactivated.');
      await reload();
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to update status');
    }
  };

  const handleDelete = async (id) => {
    const ok = await confirm({
      title: 'Remove engagement service',
      message: removeConfirmMessage('engagement service'),
      confirmText: 'Remove',
      danger: true
    });
    if (!ok) return;
    try {
      await deleteEngagementService(id);
      showSuccess('Engagement service removed.');
      await reload();
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to remove engagement service');
    }
  };

  const handleDuplicate = async (item) => {
    try {
      await duplicateEngagementService(item._id);
      showSuccess('Engagement service duplicated into your catalog.');
      await reload();
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to duplicate engagement service');
    }
  };

  const catalogActions = (item) => (
    <CatalogItemActions
      portal={portal}
      item={item}
      onEdit={handleEdit}
      onDelete={handleDelete}
      onDuplicate={handleDuplicate}
      onToggle={handleToggle}
    />
  );

  const serviceColumns = useMemo(
    () => [
      { key: 'title', label: 'Title', render: (item) => <strong>{item.title}</strong> },
      {
        key: 'linkUrl',
        label: 'URL',
        render: (item) => <ExpandableText text={item.linkUrl} lines={1} minCharsForToggle={60} />
      },
      {
        key: 'appId',
        label: 'App ID',
        render: (item) => (item.appId ? <code>{item.appId}</code> : '-')
      },
      {
        key: 'description',
        label: 'Description',
        render: (item) =>
          item.description ? <ExpandableText text={item.description} lines={2} /> : '-'
      },
      { key: 'displayOrder', label: 'Order', render: (item) => item.displayOrder ?? 0 },
      { key: 'visibility', label: 'Visibility', render: (item) => formatContentScopeLabel(item) },
      {
        key: 'status',
        label: 'Status',
        render: (item) => (
          <span className={`status-badge ${item.isEnabled ? 'active' : 'inactive'}`}>
            {item.isEnabled ? 'Active' : 'Inactive'}
          </span>
        )
      },
      {
        key: 'actions',
        label: 'Actions',
        render: (item) => catalogActions(item)
      }
    ],
    [portal]
  );

  const serviceListFooter = (
    <>
      {loadingMore && <p className="admin-infinite-loading">Loading more…</p>}
      {!pagination.hasMore && services.length > 0 && (
        <p className="admin-infinite-end">All items loaded ({pagination.total} total).</p>
      )}
    </>
  );

  return (
    <AdminLayout
      pageTitle="Engagement Services"
      subtitle={
        pagination.total
          ? `${services.length} of ${pagination.total} items · scroll for more`
          : 'Apps and links users can explore during a branch visit'
      }
    >
      <div className="admin-page-container">
        <div className="admin-page-header">
          <h1>Engagement Service List</h1>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <input
              type="search"
              className="admin-search-input"
              placeholder="Search by title…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ minWidth: 200 }}
            />
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                setEditingItem(null);
                setFormData(makeInitialForm(portal));
                setShowForm(true);
              }}
            >
              + Add Engagement Service
            </button>
          </div>
        </div>

        {!portal.isBranchAdmin && (
        <ContentScopeFields
          variant="filter"
          value={filters}
          onChange={setFilters}
          brands={portal.brands}
          salons={portal.salons}
          lockedBrandId={portal.lockBrandId}
          lockedSalonId={portal.lockSalonId}
          allowedScopes={contentScopeAllowedScopes(portal)}
        />
        )}

        {showForm && (
          <div className="admin-card" style={{ marginBottom: '24px' }}>
            <h2>{editingItem ? 'Edit Engagement Service' : 'Create Engagement Service'}</h2>
            <form ref={formRef} onSubmit={handleSubmit} noValidate>
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  className={formErrors.title ? 'input-error' : undefined}
                  required
                  onChange={(e) => {
                    setFormData({ ...formData, title: e.target.value });
                    if (formErrors.title) setFormErrors((prev) => ({ ...prev, title: '' }));
                  }}
                />
                {formErrors.title && <span className="form-error-text">{formErrors.title}</span>}
              </div>
              <div className="form-group">
                <label>Link URL *</label>
                <input
                  type="text"
                  inputMode="url"
                  autoComplete="url"
                  placeholder="https://…"
                  value={formData.linkUrl}
                  className={formErrors.linkUrl ? 'input-error' : undefined}
                  required
                  onChange={(e) => {
                    setFormData({ ...formData, linkUrl: e.target.value });
                    if (formErrors.linkUrl) setFormErrors((prev) => ({ ...prev, linkUrl: '' }));
                  }}
                />
                {formErrors.linkUrl && <span className="form-error-text">{formErrors.linkUrl}</span>}
                <p className="form-hint">
                  Deep link or web URL opened in the app when installed, otherwise in the mirror browser.
                </p>
              </div>
              <div className="form-group">
                <label>Android app ID (optional)</label>
                <input
                  type="text"
                  autoComplete="off"
                  placeholder="com.netflix.ninja"
                  value={formData.appId}
                  className={formErrors.appId ? 'input-error' : undefined}
                  onChange={(e) => {
                    setFormData({ ...formData, appId: e.target.value });
                    if (formErrors.appId) setFormErrors((prev) => ({ ...prev, appId: '' }));
                  }}
                />
                {formErrors.appId && <span className="form-error-text">{formErrors.appId}</span>}
                <p className="form-hint">
                  Package name of the TV/Android app. When installed on the mirror, Link URL opens in that app;
                  if missing or not installed, the link opens in the in-app browser.
                </p>
              </div>
              <div className="form-group">
                <label>Description</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>
              <MediaUploadInput
                label="Thumbnail URL"
                value={formData.thumbnailUrl}
                externalError={formErrors.thumbnailUrl || ''}
                onChange={(v) => {
                  setFormData({ ...formData, thumbnailUrl: v });
                  if (formErrors.thumbnailUrl) setFormErrors((prev) => ({ ...prev, thumbnailUrl: '' }));
                }}
                accept="image/*"
                maxSizeMb={2}
                placeholder="https://…"
                helperText="Paste URL or drag & drop image file. Recommended: <= 2MB."
              />
              <div className="form-row">
                <div className="form-group">
                  <label>Display Order</label>
                  <input
                    type="number"
                    value={formData.displayOrder}
                    onChange={(e) => setFormData({ ...formData, displayOrder: e.target.value })}
                  />
                </div>
              </div>
              <ContentScopeFields
                value={formData}
                onChange={(scopeFields) => {
                  setFormData((prev) => ({ ...prev, ...scopeFields }));
                  if (formErrors.brands || formErrors.salons) {
                    setFormErrors((prev) => ({ ...prev, brands: '', salons: '' }));
                  }
                }}
                brands={portal.brands}
                salons={portal.salons}
                lockedBrandId={portal.lockBrandId}
                lockedSalonId={portal.lockSalonId}
                allowedScopes={contentScopeAllowedScopes(portal)}
              />
              {(formErrors.brands || formErrors.salons) && (
                <p className="form-error-text">{formErrors.brands || formErrors.salons}</p>
              )}
              <div className="form-group">
                <label className="admin-checkbox-label admin-checkbox-label--row">
                  <input
                    type="checkbox"
                    className="admin-checkbox-input"
                    checked={Boolean(formData.isEnabled)}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, isEnabled: e.target.checked }))
                    }
                    aria-label="Engagement service is enabled"
                  />
                  <span>Active</span>
                </label>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary">
                  {editingItem ? 'Update Engagement Service' : 'Create Engagement Service'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {loading && services.length === 0 ? (
          <Loader variant="dark" fullScreen={false} />
        ) : (
          <>
            <AdminListViewControls
              viewMode={listView.viewMode}
              onViewModeChange={listView.setViewMode}
              selectedCount={listView.selectedCount}
              onSelectAll={listView.selectAll}
              onClearSelection={listView.clearSelection}
              allSelected={listView.allSelected}
              someSelected={listView.someSelected}
              totalCount={services.length}
            showSelection={false}
            />
            <AdminSelectableList
              items={services}
              getItemId={(item) => item._id}
              viewMode={listView.viewMode}
            selectable={false}
              isSelected={listView.isSelected}
              onToggleItem={listView.toggleItem}
              allSelected={listView.allSelected}
              someSelected={listView.someSelected}
              onSelectAll={listView.selectAll}
              onClearSelection={listView.clearSelection}
              columns={serviceColumns}
              renderGridCard={(item) => (
                <div className="admin-card admin-card--stretch">
                  <div className="card-header">
                    <div>
                      <h3>{item.title}</h3>
                      <p>
                        <strong>URL:</strong>{' '}
                        <ExpandableText text={item.linkUrl} lines={1} minCharsForToggle={60} />
                      </p>
                      {item.appId ? (
                        <p>
                          <strong>App ID:</strong> <code>{item.appId}</code>
                        </p>
                      ) : null}
                      {item.description ? (
                        <p>
                          <strong>Description:</strong> <ExpandableText text={item.description} lines={2} />
                        </p>
                      ) : null}
                      <p><strong>Order:</strong> {item.displayOrder ?? 0}</p>
                      <p><strong>Visibility:</strong> {formatContentScopeLabel(item)}</p>
                      <p>
                        <strong>Status:</strong>{' '}
                        <span className={`status-badge ${item.isEnabled ? 'active' : 'inactive'}`}>
                          {item.isEnabled ? 'Active' : 'Inactive'}
                        </span>
                      </p>
                    </div>
                    {catalogActions(item)}
                  </div>
                </div>
              )}
              emptyState={
                <div className="empty-state">
                  <p>No engagement services found. Add your first item.</p>
                </div>
              }
              scrollRef={scrollRef}
              footer={serviceListFooter}
            />
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default EngagementServiceManagement;
